/**
 * Business Profile API Client
 */

import { apiPost } from './client';

// ============================================
// Types
// ============================================

export interface BusinessProfile {
  version: string;
  generatedAt: string;
  confirmedAt?: string;

  industry: {
    primary: string;
    specific: string;
    confidence: number;
  };

  coreSummary: string;
  services: string[];
  atmosphere: string;
  visualStyle: string;
  tone: string;
  idealClient: string;
  uniqueValue: string;
  imageKeywords: string[];
  imageAvoid: string[];
}

export interface ClarificationThread {
  messages: Array<{
    role: 'customer' | 'assistant';
    content: string;
    timestamp: string;
    profileSnapshot?: BusinessProfile;
  }>;
  escalatedAt?: string;
  escalationReason?: 'customer_requested' | 'max_rounds';
}

// ============================================
// API Functions
// ============================================

/**
 * Generate business profile from onboarding data
 */
export async function generateProfile(sessionId: string): Promise<{
  profile: BusinessProfile;
  clarificationThread: ClarificationThread | null;
  clarificationCount: number;
  isExisting: boolean;
}> {
  const response = await apiPost<{
    success: boolean;
    profile: BusinessProfile;
    clarificationThread: ClarificationThread | null;
    clarificationCount: number;
    isExisting: boolean;
  }>(`/api/onboarding/${sessionId}/generate-profile`, {});

  return {
    profile: response.profile,
    clarificationThread: response.clarificationThread,
    clarificationCount: response.clarificationCount,
    isExisting: response.isExisting,
  };
}

/**
 * Clarify profile based on customer feedback
 */
export async function clarifyProfile(
  sessionId: string,
  currentProfile: BusinessProfile,
  customerFeedback: string,
  clarificationCount: number
): Promise<{
  profile: BusinessProfile;
  assistantMessage: string;
  clarificationCount: number;
  nextRoundMessage: string;
  showEscalateOption: boolean;
  forceEscalate: boolean;
}> {
  const response = await apiPost<{
    success: boolean;
    profile: BusinessProfile;
    assistantMessage: string;
    clarificationCount: number;
    nextRoundMessage: string;
    showEscalateOption: boolean;
    forceEscalate: boolean;
  }>(`/api/onboarding/${sessionId}/clarify-profile`, {
    currentProfile,
    customerFeedback,
    clarificationCount,
  });

  return {
    profile: response.profile,
    assistantMessage: response.assistantMessage,
    clarificationCount: response.clarificationCount,
    nextRoundMessage: response.nextRoundMessage,
    showEscalateOption: response.showEscalateOption,
    forceEscalate: response.forceEscalate,
  };
}

/**
 * Confirm the profile is accurate
 */
export async function confirmProfile(
  sessionId: string,
  profile: BusinessProfile
): Promise<{ profile: BusinessProfile }> {
  const response = await apiPost<{
    success: boolean;
    profile: BusinessProfile;
  }>(`/api/onboarding/${sessionId}/confirm-profile`, { profile });

  return { profile: response.profile };
}

/**
 * Escalate to human support
 */
export async function escalateProfile(
  sessionId: string,
  profile: BusinessProfile,
  clarificationThread: ClarificationThread,
  reason: 'customer_requested' | 'max_rounds'
): Promise<{ message: string }> {
  const response = await apiPost<{
    success: boolean;
    message: string;
  }>(`/api/onboarding/${sessionId}/escalate-profile`, {
    profile,
    clarificationThread,
    reason,
  });

  return { message: response.message };
}
